import { useEffect, useRef } from "react";
import {
  FontAwesomeIcon,
  FontAwesomeIconProps,
} from "@fortawesome/react-fontawesome";
import styles from "./ExtraUserOptions.module.scss";

interface Option {
  name: string;
  icon: FontAwesomeIconProps["icon"];
  color?: string;
}

interface ExtraUserOptionsProps {
  options: Option[];
  currentPosition: number;
}

export default function ExtraUserOptions({
  options,
  currentPosition,
}: ExtraUserOptionsProps) {
  const listRef = useRef<HTMLDivElement | null>(null);
  const optionRefs = useRef<HTMLDivElement[]>([]);
  const rowRef = useRef<HTMLDivElement | null>(null);

  // Consolidated centering logic
  const updateCentering = () => {
    const container = listRef.current;
    const row = rowRef.current;
    const item = optionRefs.current[currentPosition];
    if (!container || !row || !item) return;

    requestAnimationFrame(() => {
      const viewportCenter = window.innerWidth / 2;
      const containerRect = container.getBoundingClientRect();
      const itemCenter = item.offsetLeft + item.offsetWidth / 2;
      const offset = viewportCenter - containerRect.left - itemCenter;
      row.style.setProperty("--extra-user-options-offset", `${offset}px`);
    });
  };

  // Update centering when position or options change
  useEffect(() => {
    updateCentering();
  }, [currentPosition, options.length]);

  // Handle window resize - shouldn't happen but can't hurt
  useEffect(() => {
    window.addEventListener("resize", updateCentering);
    return () => window.removeEventListener("resize", updateCentering);
  }, [currentPosition, options.length]);

  return (
    <div ref={listRef}>
      <div className={styles.row} ref={rowRef}>
        {options.map((option, index) => (
          <div
            key={option.name}
            className={`${styles.option} ${
              index === currentPosition ? styles.selected : ""
            }`}
            ref={(el) => {
              if (el) optionRefs.current[index] = el;
            }}
          >
            <div
              className={styles.icon}
              style={
                {
                  ["--extra-user-option-icon-color"]:
                    index === currentPosition ? option?.color : "white",
                } as React.CSSProperties
              }
            >
              <FontAwesomeIcon icon={option.icon} />
            </div>
            <p className={styles.name}>{option.name}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
